import React, { useCallback, useState, useEffect } from 'react';
import CenteredModal from '../ui/CenteredModal';
import AssignmentDetails from '../assignments/AssignmentDetails';
import EventDetails from '../events/EventDetails';
import { normEndeavorId, normAssignmentId } from '../../contexts/dataUtils';
import { useData } from '../../contexts/DataContext';
import './TileDetailModal.css';

/**
 * TileDetailModal — drill-down modal for KPI tiles (Overdue, Due 7d, Completed 7d).
 *
 * - Rows open inline AssignmentDetails / EventDetails modals (no page navigation).
 * - Checkmarks toggle completion optimistically via DataContext.
 *
 * Props:
 *   mode:  'overdue' | 'due' | 'completed'
 *   items: array from useAcademicHealth (overdueItems / dueNext7dItems / completedLast7dItems)
 *   isOpen: boolean
 *   onClose: () => void
 */
export default function TileDetailModal({ mode, items, isOpen, onClose }) {
  const dataContext = useSafeData();
  const coursesById = dataContext?.state?.coursesById || {};
  const toggleAssignmentCompleted = dataContext?.toggleAssignmentCompleted;
  const markAssignmentCompleted = dataContext?.markAssignmentCompleted;
  const bulkMarkAssignmentsCompleted = dataContext?.bulkMarkAssignmentsCompleted;
  const updateAssignment = dataContext?.updateAssignment;
  const deleteAssignment = dataContext?.deleteAssignment;
  const clearCache = dataContext?.clearCache;
  const updateCachedAssignment = dataContext?.updateCachedAssignment;

  // Detail modal state
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Optimistic local completion state — maps assignment ID → boolean override
  const [localCompleted, setLocalCompleted] = useState({});
  const [togglingIds, setTogglingIds] = useState(new Set());
  const [markingAll, setMarkingAll] = useState(false);

  // Reset optimistic state when upstream data changes (sync from external sources)
  useEffect(() => {
    setLocalCompleted({});
  }, [items]);

  const title = {
    overdue:   `Overdue (${items.length})`,
    due:       `Due Next 7 Days (${items.length})`,
    completed: `Completed — Last 7 Days (${items.length})`,
  }[mode] || `Tasks (${items.length})`;

  // ── Open detail modal (no navigation) ──
  const handleRowClick = useCallback((a) => {
    // These items are always assignments (from useAcademicHealth).
    // They may have an eventId linking them to a parent event, but
    // clicking a row should always open the assignment detail.
    setSelectedAssignment(a);
  }, []);

  // ── Toggle completion optimistically ──
  const handleToggle = useCallback(async (a, e) => {
    e.stopPropagation();
    if (!toggleAssignmentCompleted) return;

    const id = normAssignmentId(a);
    if (!id || togglingIds.has(id)) return;

    const currentlyDone = localCompleted[id] ?? isCompleted(a);
    const willBeDone = !currentlyDone;

    // Optimistic UI update
    setLocalCompleted(prev => ({ ...prev, [id]: willBeDone }));
    setTogglingIds(prev => new Set(prev).add(id));

    try {
      await toggleAssignmentCompleted(a, { source: 'tile-detail-modal' });
      window.dispatchEvent(new Event('assignments:updated'));
    } catch (err) {
      // Revert on failure
      setLocalCompleted(prev => ({ ...prev, [id]: currentlyDone }));
      console.error('Toggle failed:', err);
    } finally {
      setTogglingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [toggleAssignmentCompleted, localCompleted, togglingIds]);

  // ── Mark all overdue as complete ──
  const handleMarkAllComplete = useCallback(async () => {
    if ((!bulkMarkAssignmentsCompleted && !toggleAssignmentCompleted) || markingAll) return;

    // Gather only items not already completed
    const pending = items.filter(item => {
      const a = item.assignment;
      const id = normAssignmentId(a);
      const done = localCompleted[id] ?? isCompleted(a);
      return !done;
    });
    if (pending.length === 0) return;

    // Optimistic: mark all as done
    const updates = {};
    const pendingIds = [];
    pending.forEach(item => {
      const id = normAssignmentId(item.assignment);
      if (id) {
        updates[id] = true;
        pendingIds.push(id);
      }
    });
    setLocalCompleted(prev => ({ ...prev, ...updates }));
    setMarkingAll(true);

    try {
      if (bulkMarkAssignmentsCompleted) {
        // Single request for all assignments
        await bulkMarkAssignmentsCompleted(pendingIds);
      } else {
        // Fallback: sequential requests
        for (const item of pending) {
          await toggleAssignmentCompleted(item.assignment, { source: 'tile-detail-modal-bulk' });
        }
      }
    } catch (err) {
      // Revert all on failure
      console.error('Bulk complete failed:', err);
      setLocalCompleted(prev => {
        const next = { ...prev };
        pendingIds.forEach(id => { next[id] = false; });
        return next;
      });
    }

    window.dispatchEvent(new Event('assignments:updated'));
    setMarkingAll(false);
  }, [items, bulkMarkAssignmentsCompleted, toggleAssignmentCompleted, localCompleted, markingAll]);

  // ── Assignment detail modal callbacks ──
  const handleAssignmentMarkComplete = useCallback(async (assignmentData) => {
    const id = assignmentData.assignmentId || assignmentData.id;
    const willBeCompleted = assignmentData.completed;
    let response;
    if (willBeCompleted && markAssignmentCompleted) {
      response = await markAssignmentCompleted(id);
    } else if (updateAssignment) {
      response = await updateAssignment(id, assignmentData);
    }
    if (clearCache) {
      clearCache('assignments');
      clearCache(`assignment_${id}`);
    }
    setSelectedAssignment(null);
    window.dispatchEvent(new Event('assignments:updated'));
    return response;
  }, [markAssignmentCompleted, updateAssignment, clearCache]);

  const handleAssignmentUpdate = useCallback((updatedData) => {
    setSelectedAssignment(updatedData);
  }, []);

  const handleAssignmentSaved = useCallback(async (serverData, id) => {
    setSelectedAssignment(null);
    if (updateCachedAssignment) { updateCachedAssignment(id, serverData); }
    window.dispatchEvent(new Event('assignments:updated'));
  }, [updateCachedAssignment]);

  const getCourseName = (a) => {
    const cid = normEndeavorId(a);
    if (!cid) return null;
    const course = coursesById[cid] || coursesById[String(cid)];
    return course?.name || course?.title || null;
  };

  return (
    <>
      <CenteredModal isOpen={isOpen} onClose={onClose} title={title}>
        <div className="tdm-modal">
          {items.length === 0 ? (
            <div className="tdm-empty">
              {mode === 'overdue' && 'No overdue assignments — nice work!'}
              {mode === 'due' && 'Nothing due in the next 7 days.'}
              {mode === 'completed' && 'No completions in the last 7 days.'}
            </div>
          ) : (
            <div className="tdm-list">
              {items.map((item, i) => {
                const a = item.assignment;
                const id = normAssignmentId(a);
                const assignmentTitle = a.title || a.name || 'Assignment';
                const courseName = getCourseName(a);
                const done = localCompleted[id] ?? isCompleted(a);
                const toggling = togglingIds.has(id);

                return (
                  <div
                    key={id || i}
                    className={`tdm-row ${mode === 'overdue' && !done ? 'tdm-row--overdue' : ''} ${done ? 'tdm-row--completed' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleRowClick(a)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRowClick(a); }}
                  >
                    {/* Checkmark toggle */}
                    <span
                      className={`tdm-check ${done ? 'tdm-check--done' : ''} ${toggling ? 'tdm-check--busy' : ''}`}
                      role="checkbox"
                      aria-checked={done}
                      aria-label={done ? 'Mark incomplete' : 'Mark complete'}
                      tabIndex={0}
                      onClick={(e) => handleToggle(a, e)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle(a, e); } }}
                    >
                      {done && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </span>

                    {/* Body */}
                    <div className="tdm-body">
                      <span className="tdm-title">{assignmentTitle}</span>
                      {courseName && <span className="tdm-course">{courseName}</span>}
                    </div>

                    {/* Date */}
                    <span className="tdm-date">
                      {mode === 'overdue' && !done && item.ageDays != null && (
                        <span className="tdm-dateOverdue">{item.ageDays}d overdue</span>
                      )}
                      {mode === 'overdue' && item.dueDate && (
                        <span className="tdm-dateActual">{fmtDate(item.dueDate)}</span>
                      )}
                      {mode === 'due' && item.dueDate && fmtDate(item.dueDate)}
                      {mode === 'completed' && item.completedDate && fmtDate(item.completedDate)}
                    </span>

                    {/* Chevron */}
                    <span className="tdm-chevron" aria-hidden="true">›</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div className="tdm-footer">
            <span className="tdm-count">{items.length} item{items.length !== 1 ? 's' : ''}</span>
            <div className="tdm-footerActions">
              {mode === 'overdue' && items.length > 0 && (
                <button
                  className="tdm-completeAllBtn"
                  onClick={handleMarkAllComplete}
                  disabled={markingAll}
                >
                  {markingAll ? 'Completing…' : 'Complete All'}
                </button>
              )}
              <button className="tdm-closeBtn" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </CenteredModal>

      {/* Assignment detail modal (stacks on top) */}
      <CenteredModal
        isOpen={!!selectedAssignment}
        onClose={() => setSelectedAssignment(null)}
        title={selectedAssignment?.title || selectedAssignment?.name}
        onDelete={selectedAssignment && deleteAssignment ? async () => {
          await deleteAssignment(selectedAssignment.assignmentId || selectedAssignment.id);
          setSelectedAssignment(null);
          window.dispatchEvent(new Event('assignments:updated'));
        } : undefined}
      >
        {selectedAssignment && (
          <AssignmentDetails
            assignment={selectedAssignment}
            onClose={() => setSelectedAssignment(null)}
            onUpdate={handleAssignmentUpdate}
            onSaved={handleAssignmentSaved}
            onMarkComplete={handleAssignmentMarkComplete}
          />
        )}
      </CenteredModal>

      {/* Event detail modal (stacks on top) */}
      <CenteredModal
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        title={selectedEvent?.title || selectedEvent?.name}
      >
        {selectedEvent && (
          <EventDetails
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
          />
        )}
      </CenteredModal>
    </>
  );
}

/* ── Helpers ── */

function isCompleted(a) {
  if (!a) return false;
  if (a.status === 'completed' || a.status === 'Completed') return true;
  if (a.completedAt != null || a.completed_at != null) return true;
  if (a.completed) return true;
  if (a.submissionStatus === 'Completed') return true;
  if (a.completionStatus === 'Completed') return true;
  return false;
}

// eslint-disable-next-line no-unused-vars
function normalizeEventForModal(event) {
  if (!event) return null;
  const start = event.start || event.startDatetime || event.startDate
    || event.start_date || event.eventDate || event.dueDate;
  const end = event.end || event.endDatetime || event.endDate || event.end_date;
  return {
    ...event, start, end,
    title: event.title || event.name || 'Untitled Event',
    location: event.location || event.eventLocation || '',
    status: event.status || '',
    description: event.description || event.eventDescription || '',
    tags: event.tags || [],
  };
}

function fmtDate(d) {
  try {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function useSafeData() {
  try {
    return useData();
  } catch { return {}; }
}
